import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Badge, Pagination, Button, ProgressBar, Row, Col, Alert } from 'react-bootstrap';
import api from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';

const NotificationLogs = () => {
  const { selectedTenant } = useTenant();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: '' });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });

  const [orchestratorStatus, setOrchestratorStatus] = useState({
    isRunning: false,
    stopRequested: false,
    counts: { pending: 0, sent: 0, failed: 0, total: 0 }
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (selectedTenant) {
      fetchLogs();
    }
  }, [filters, pagination.page, selectedTenant]);

  useEffect(() => {
    let intervalId = null;
    if (selectedTenant) {
      fetchStatus();
      if (orchestratorStatus.isRunning) {
        intervalId = setInterval(fetchStatus, 2000);
      }
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [orchestratorStatus.isRunning, selectedTenant]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.status && { status: filters.status }),
      };
      const { data } = await api.get('/notifications/logs', { params });
      setLogs(data.logs);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('Error fetching notification logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const { data } = await api.get('/notifications/status');
      setOrchestratorStatus(data);
    } catch (error) {
      console.error('Error fetching orchestrator status:', error);
    }
  };

  const handleRun = async () => {
    setActionLoading(true);
    try {
      await api.post('/notifications/run');
      setTimeout(() => {
        fetchStatus();
        fetchLogs();
      }, 500);
    } catch (error) {
      console.error('Error starting reminders:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    try {
      await api.post('/notifications/stop');
      setTimeout(fetchStatus, 500);
    } catch (error) {
      console.error('Error stopping reminders:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={pagination.page === 1}
        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
      />
    );

    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === pagination.page}
          onClick={() => setPagination(prev => ({ ...prev, page: i }))}
        >
          {i}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={pagination.page === totalPages}
        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
      />
    );

    return <Pagination className="mb-0 justify-content-center">{items}</Pagination>;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Notificaciones de Pago</h2>
      </div>

      {/* Alertas de Configuración del Tenant */}
      {orchestratorStatus.config && !orchestratorStatus.config.enabled && (
        <Alert variant="warning" className="mb-4 d-flex align-items-center">
          <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
          <div>
            <strong className="d-block">Notificaciones automáticas deshabilitadas</strong>
            Las notificaciones automatizadas de recordatorios de pago para esta institución están apagadas en la configuración del sistema.
          </div>
        </Alert>
      )}

      {orchestratorStatus.config && orchestratorStatus.config.enabled && (
        <Alert variant="info" className="mb-4 d-flex align-items-center">
          <i className="bi bi-info-circle-fill fs-4 me-3"></i>
          <div>
            <strong className="d-block">Notificaciones automáticas activadas</strong>
            Las notificaciones automáticas se enviarán periódicamente cada <strong>{orchestratorStatus.config.periodDays} días</strong> a los tutores con adeudos pendientes.
          </div>
        </Alert>
      )}

      {/* Panel de Control y Progreso */}
      <Card className="mb-4 shadow-sm border-0 bg-light">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={8}>
              <h4 className="mb-2 text-dark font-weight-bold">Control de Envío Manual</h4>
              <p className="text-muted mb-0 small">
                Inicia el envío inmediato de recordatorios a todos los tutores con adeudos pendientes. El proceso se ejecuta en segundo plano.
              </p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              {orchestratorStatus.isRunning ? (
                <Button 
                  variant="danger" 
                  onClick={handleStop} 
                  disabled={actionLoading || orchestratorStatus.stopRequested}
                  className="px-4 py-2 font-weight-bold"
                >
                  {orchestratorStatus.stopRequested ? (
                    <span><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Deteniendo...</span>
                  ) : (
                    <span>Detener Envío</span>
                  )}
                </Button>
              ) : (
                <Button 
                  variant="primary" 
                  onClick={handleRun} 
                  disabled={actionLoading || (orchestratorStatus.config && !orchestratorStatus.config.enabled)}
                  className="px-4 py-2 font-weight-bold"
                >
                  Iniciar Recordatorios
                </Button>
              )}
            </Col>
          </Row>

          {(orchestratorStatus.isRunning || orchestratorStatus.counts.total > 0) && (
            <div className="mt-4 pt-3 border-top">
              <Row className="mb-3 text-center">
                <Col xs={3}>
                  <div className="text-muted small">Total</div>
                  <h3 className="font-weight-bold text-dark">{orchestratorStatus.counts.total}</h3>
                </Col>
                <Col xs={3}>
                  <div className="text-warning small">Pendientes</div>
                  <h3 className="font-weight-bold text-warning">{orchestratorStatus.counts.pending}</h3>
                </Col>
                <Col xs={3}>
                  <div className="text-success small">Enviados</div>
                  <h3 className="font-weight-bold text-success">{orchestratorStatus.counts.sent}</h3>
                </Col>
                <Col xs={3}>
                  <div className="text-danger small">Fallidos</div>
                  <h3 className="font-weight-bold text-danger">{orchestratorStatus.counts.failed}</h3>
                </Col>
              </Row>

              {orchestratorStatus.counts.total > 0 && (
                <div>
                  <ProgressBar className="mb-2" style={{ height: '10px' }}>
                    <ProgressBar 
                      variant="success" 
                      now={(orchestratorStatus.counts.sent / orchestratorStatus.counts.total) * 100} 
                      key={1} 
                    />
                    <ProgressBar 
                      variant="danger" 
                      now={(orchestratorStatus.counts.failed / orchestratorStatus.counts.total) * 100} 
                      key={2} 
                    />
                    <ProgressBar 
                      variant="warning" 
                      now={(orchestratorStatus.counts.pending / orchestratorStatus.counts.total) * 100} 
                      key={3} 
                      animated={orchestratorStatus.isRunning}
                    />
                  </ProgressBar>
                  <div className="d-flex justify-content-between text-muted small">
                    <span>
                      Progreso: {Math.round(((orchestratorStatus.counts.sent + orchestratorStatus.counts.failed) / orchestratorStatus.counts.total) * 100)}%
                    </span>
                    {orchestratorStatus.isRunning && (
                      <span className="text-primary font-italic">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Procesando recordatorios activos...
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="bg-light">
          <Form className="d-flex gap-3 align-items-end flex-wrap">
            <Form.Group>
              <Form.Label className="small mb-1">Fecha inicio</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="small mb-1">Fecha fin</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="small mb-1">Estado</Form.Label>
              <Form.Select
                size="sm"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="sent">Enviado</option>
                <option value="failed">Fallido</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-bell-slash text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">Sin registros</h5>
              <p className="text-muted">No se encontraron notificaciones con los filtros seleccionados</p>
            </div>
          ) : (
            <>
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Tutor</th>
                    <th>Título</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td>{log.tutor_name}</td>
                      <td>{log.title}</td>
                      <td>
                        <Badge bg={log.status === 'sent' ? 'success' : log.status === 'pending' ? 'warning' : 'danger'}>
                          {log.status === 'sent' ? 'Enviado' : log.status === 'pending' ? 'Pendiente' : 'Fallido'}
                        </Badge>
                      </td>
                      <td>{new Date(log.created_at).toLocaleDateString('es-MX')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="p-3 border-top">
                {renderPagination()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default NotificationLogs;

